import axios, { AxiosError, AxiosResponse, AxiosRequestConfig } from 'axios'
import { logError, ErrorType, ErrorSeverity, isDatabaseError, isCacheError, logDatabaseError, logCacheError } from '../lib/errorHandler'
import { getAuthHeaders } from './workosAuth'
import { handle401 } from './authManager'

import { API_BASE_URL } from '../config';

const IS_DEV = import.meta.env.DEV

const client = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Send cookies on cross-origin requests (session_token cookie to api.edgetechnologies.com.au)
  withCredentials: true,
  timeout: 30000, // 30 second timeout
})

// Request interceptor: add auth headers (Bearer token from sessionStorage / WorkOS).
// Must be async to await getAuthHeaders() — Axios supports async request interceptors.
client.interceptors.request.use(
  async (config) => {
    // Add timestamp for debugging
    config.headers['X-Request-Time'] = new Date().toISOString()

    // Add client ID for debugging
    config.headers['X-Client-ID'] = 'ppss-ai-frontend'

    // Attach Bearer token (WorkOS access token from sessionStorage fragment handoff or getter)
    try {
      const authHeaders = await getAuthHeaders()
      const auth = authHeaders && typeof authHeaders === 'object' && 'Authorization' in authHeaders
        ? (authHeaders as { Authorization: string }).Authorization
        : null
      if (auth) config.headers['Authorization'] = auth
    } catch {
      // If token retrieval fails, continue without auth — request will get 401 from backend
    }

    // Log request in dev mode
    if (IS_DEV) {
      console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`, config.data || '')
    }

    return config
  },
  (error) => {
    logError(error, ErrorType.API, ErrorSeverity.ERROR, { stage: 'request' })
    return Promise.reject(error)
  }
)

// Response interceptor: 401 → single-flight refresh, retry once; then log other errors
client.interceptors.response.use(
  (response: AxiosResponse) => {
    if (IS_DEV) {
      console.log(`API Response: ${response.status} ${response.config.url}`, response.data)
    }
    if (response.data?.source === 'cache') {
      console.warn('Data was served from cache, not database', response.config.url)
    }
    return response
  },
  async (error: AxiosError) => {
    if (error.response?.status === 401 && error.config) {
      const recovered = await handle401()
      if (recovered) {
        const authHeaders = await getAuthHeaders()
        const auth = authHeaders && typeof authHeaders === 'object' && 'Authorization' in authHeaders
          ? (authHeaders as { Authorization: string }).Authorization
          : null
        if (auth && error.config.headers) {
          const h = error.config.headers as Record<string, string>
          h.Authorization = auth
        }
        return client.request(error.config)
      }
    }
    if (isDatabaseError(error)) {
      logDatabaseError(error, { url: error.config?.url, method: error.config?.method })
    } else if (isCacheError(error)) {
      logCacheError(error, { url: error.config?.url, method: error.config?.method })
    } else {
      logError(error, ErrorType.API, ErrorSeverity.ERROR, {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
      })
    }
    return Promise.reject(error)
  }
)

// Enhanced API methods with better error handling
const apiClient = {
  // Projects
  getProjects: async () => {
    try {
      const response = await client.get('/projects')
      return response.data
    } catch (error) {
      throw new Error("Unable to retrieve projects. Please try again later.")
    }
  },
  
  getProject: async (id: number) => {
    try {
      const response = await client.get(`/projects/${id}`)
      return response.data
    } catch (error) {
      throw new Error(`Unable to retrieve project details. Please try again later.`)
    }
  },
  
  createProject: async (data: any) => {
    try {
      const response = await client.post('/projects', data)
      return response.data
    } catch (error) {
      throw new Error("Failed to create project. Please check your input and try again.")
    }
  },
  
  // Schedule Versions
  getScheduleVersions: async (projectId: number) => {
    try {
      const response = await client.get(`/projects/${projectId}/versions`)
      return response.data
    } catch (error) {
      throw new Error("Unable to retrieve schedule versions. Please try again later.")
    }
  },
  
  createScheduleVersion: async (projectId: number, data: any) => {
    try {
      const response = await client.post(`/projects/${projectId}/versions`, data)
      return response.data
    } catch (error) {
      throw new Error("Failed to create schedule version. Please try again later.")
    }
  },
  
  // Activities
  getActivities: async (versionId: number) => {
    try {
      const response = await client.get(`/schedule-versions/${versionId}/activities`)
      return response.data
    } catch (error) {
      throw new Error("Unable to retrieve activities. Please try again later.")
    }
  },
  
  updateActivity: async (activityId: number, data: any) => {
    try {
      const response = await client.patch(`/activities/${activityId}`, data)
      return response.data
    } catch (error) {
      throw new Error("Failed to update activity. Please try again later.")
    }
  },
  
  // XER specific endpoints
  uploadXerFile: async (file: File, _userId?: number) => {
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('project_name', 'Uploaded Project')
      formData.append('project_description', '')
      formData.append('organization', 'Edge Technologies')
      formData.append('project_type', 'Current')
      
      const response = await client.post('/xer/working-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      
      return response.data
    } catch (error: any) {
      // Special handling for XER upload errors
      if (error.response?.status === 400 || 
          error.response?.data?.detail?.includes('parse') || 
          error.response?.data?.detail?.includes('invalid')) {
        throw new Error('The file you uploaded appears to be corrupted or in an invalid format. Please check the file and try again.')
      } else if (error.response?.status === 413) {
        throw new Error('The file you uploaded is too large. Please try uploading a smaller file.')
      } else if (isDatabaseError(error)) {
        throw new Error('We were unable to process your file. Our team has been notified of this issue.')
      } else if (isCacheError(error)) {
        throw new Error('There was an issue processing your file. Please try again.')
      }
      
      throw new Error('Failed to upload file. Please try again later.')
    }
  },
  
  // XER Projects API (Phase 3: no import_id in query; use project_id and optional version_id only)
  getXerProjects: async (page = 1, limit = 20, _importId?: string) => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      const response = await client.get(`/projects/?${params}`)
      return response.data
    } catch (error) {
      throw new Error('Unable to retrieve XER projects. Please try again later.')
    }
  },

  getXerProject: async (projId: number, _importId?: string) => {
    try {
      const response = await client.get(`/projects/${projId}`)
      return response.data
    } catch (error) {
      throw new Error('Unable to retrieve XER project details. Please try again later.')
    }
  },

  getXerProjectDashboard: async (projId: number, _importId?: string) => {
    try {
      const response = await client.get(`/projects/${projId}/dashboard`)
      return response.data
    } catch (error) {
      throw new Error('Unable to retrieve XER project dashboard. Please try again later.')
    }
  },

  getXerActivities: async (projId: number, options: any = {}) => {
    try {
      const params = new URLSearchParams({
        page: (options.page || 1).toString(),
        limit: (options.limit || 50).toString(),
      });
      if (options.critical !== undefined) params.set('critical', options.critical.toString());
      if (options.search) params.set('search', options.search);
      if (options.version_id) params.set('version_id', options.version_id);
      const response = await client.get(`/projects/${projId}/activities?${params}`)
      return response.data
    } catch (error) {
      throw new Error('Unable to retrieve XER activities. Please try again later.')
    }
  },

  // XER Upload API
  uploadXerFileNew: async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await client.post('/xer-upload/xer', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error('Invalid file format. Please upload a .xer file.');
      } else if (error.response?.status === 413) {
        throw new Error('File too large. Maximum size is 100MB.');
      }
      throw new Error('Failed to upload XER file. Please try again later.');
    }
  },

  getXerUploadStatus: async (importId: string) => {
    try {
      const response = await client.get(`/xer-upload/status/${importId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw new Error('Unable to retrieve upload status. Please try again later.');
    }
  },

  // Generic HTTP methods for direct API access
  get: (url: string, config?: AxiosRequestConfig) => client.get(url, config),
  post: (url: string, data?: unknown, config?: AxiosRequestConfig) => client.post(url, data, config),
  put: (url: string, data?: unknown, config?: AxiosRequestConfig) => client.put(url, data, config),
  patch: (url: string, data?: unknown, config?: AxiosRequestConfig) => client.patch(url, data, config),
  delete: (url: string, config?: AxiosRequestConfig) => client.delete(url, config),
}

export default apiClient 